import React, { useState, useMemo } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import './shared.css';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const startOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

const addDays = (date, n) => {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
};

const sameDay = (a, b) =>
  a.getDate() === b.getDate() &&
  a.getMonth() === b.getMonth() &&
  a.getFullYear() === b.getFullYear();

const JobCalendar = ({ events = [], onEventClick, onDateChange }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month');

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const monthDays = useMemo(() => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    let startDay = firstDay.getDay() - 1;
    if (startDay < 0) startDay = 6;

    const days = [];
    const prevMonth = new Date(year, month, 0);
    for (let i = startDay - 1; i >= 0; i--) {
      days.push({ day: prevMonth.getDate() - i, currentMonth: false, date: new Date(year, month - 1, prevMonth.getDate() - i) });
    }
    for (let d = 1; d <= lastDay.getDate(); d++) {
      days.push({ day: d, currentMonth: true, date: new Date(year, month, d) });
    }
    const remaining = 42 - days.length;
    for (let d = 1; d <= remaining; d++) {
      days.push({ day: d, currentMonth: false, date: new Date(year, month + 1, d) });
    }
    return days;
  }, [year, month]);

  const weekDays = useMemo(() => {
    const start = startOfWeek(currentDate);
    return Array.from({ length: 7 }, (_, i) => {
      const date = addDays(start, i);
      return { day: date.getDate(), currentMonth: date.getMonth() === month, date };
    });
  }, [currentDate, month]);

  const days = view === 'month' ? monthDays : weekDays;

  const eventsByDate = useMemo(() => {
    const map = {};
    events.forEach(e => {
      const key = new Date(e.date).toISOString().split('T')[0];
      if (!map[key]) map[key] = [];
      map[key].push(e);
    });
    return map;
  }, [events]);

  const emitDateChange = (date, mode) => {
    if (!onDateChange) return;
    if (mode === 'month') {
      const start = new Date(date.getFullYear(), date.getMonth(), 1).toISOString().split('T')[0];
      const end = new Date(date.getFullYear(), date.getMonth() + 1, 0).toISOString().split('T')[0];
      onDateChange(start, end);
    } else {
      const s = startOfWeek(date);
      const e = addDays(s, 6);
      onDateChange(s.toISOString().split('T')[0], e.toISOString().split('T')[0]);
    }
  };

  const navigate = (dir) => {
    let newDate;
    if (view === 'month') {
      newDate = new Date(year, month + dir, 1);
    } else {
      newDate = addDays(currentDate, dir * 7);
    }
    setCurrentDate(newDate);
    emitDateChange(newDate, view);
  };

  const goToday = () => {
    const today = new Date();
    setCurrentDate(today);
    emitDateChange(today, view);
  };

  const switchView = (next) => {
    if (next === view) return;
    setView(next);
    emitDateChange(currentDate, next);
  };

  const isToday = (date) => sameDay(date, new Date());

  const headerLabel = (() => {
    if (view === 'month') return `${MONTHS[month]} ${year}`;
    const s = startOfWeek(currentDate);
    const e = addDays(s, 6);
    const sM = MONTHS[s.getMonth()].slice(0, 3);
    const eM = MONTHS[e.getMonth()].slice(0, 3);
    if (s.getMonth() === e.getMonth()) {
      return `${sM} ${s.getDate()}–${e.getDate()}, ${e.getFullYear()}`;
    }
    return `${sM} ${s.getDate()} – ${eM} ${e.getDate()}, ${e.getFullYear()}`;
  })();

  return (
    <div className="cs-calendar">
      <div className="cs-cal-header">
        <div className="cs-cal-header-left">
          <button className="cs-cal-nav" onClick={() => navigate(-1)} aria-label="Previous"><FaChevronLeft /></button>
          <button className="cs-cal-today" onClick={goToday} type="button">Today</button>
          <button className="cs-cal-nav" onClick={() => navigate(1)} aria-label="Next"><FaChevronRight /></button>
        </div>
        <h3>{headerLabel}</h3>
        <div className="cs-cal-view-toggle" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={view === 'week'}
            className={`cs-cal-view-btn ${view === 'week' ? 'active' : ''}`}
            onClick={() => switchView('week')}
          >
            Week
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === 'month'}
            className={`cs-cal-view-btn ${view === 'month' ? 'active' : ''}`}
            onClick={() => switchView('month')}
          >
            Month
          </button>
        </div>
      </div>

      <div className={`cs-cal-grid ${view === 'week' ? 'view-week' : 'view-month'}`}>
        {DAYS.map(d => <div key={d} className="cs-cal-day-label">{d}</div>)}
        {days.map((d, i) => {
          const dateKey = d.date.toISOString().split('T')[0];
          const dayEvents = eventsByDate[dateKey] || [];
          const visibleLimit = view === 'week' ? 8 : 3;
          return (
            <div key={i} className={`cs-cal-cell ${!d.currentMonth ? 'other-month' : ''} ${isToday(d.date) ? 'today' : ''}`}>
              <span className="cs-cal-day-num">{d.day}</span>
              <div className="cs-cal-events">
                {dayEvents.slice(0, visibleLimit).map(ev => (
                  <div
                    key={ev.id}
                    className={`cs-cal-event ${ev.tentative ? 'tentative' : ''} status-${ev.status}`}
                    onClick={() => onEventClick && onEventClick(ev)}
                    title={`${ev.title} - ${ev.propertyName || ''}`}
                  >
                    <span className="cs-cal-event-text">{ev.title}</span>
                  </div>
                ))}
                {dayEvents.length > visibleLimit && (
                  <span className="cs-cal-more">+{dayEvents.length - visibleLimit} more</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default JobCalendar;
